import { take, forkJoin } from "rxjs";
import {
  FetchRefundRequestsDocument,
  SetRefundRequestStatusDocument,
} from "../generated-graphql/graphql";
import { marker as _ } from "@biesbjerg/ngx-translate-extract-marker";
import {
  DataService,
  SharedModule,
  TypedBaseListComponent,
  ModalService,
  OrderDetailFragment,
  NotificationService,
  LocaleCurrencyPipe,
  CurrencyCode,
  CurrencyService,
} from "@bb-vendure/admin-ui/core";
import { Component, OnInit } from "@angular/core";
import {
  StatusColorPipe,
} from "../Pipe";
import ModalRefundDetailComponent from "../request-refund-dialog/modal.component";

@Component({
  standalone: true,
  imports: [
    SharedModule,
    StatusColorPipe,
  ],
  templateUrl: "./request-refund-list.component.html",
  styleUrls: ["./request-refund-list.component.scss"],
})
export default class RequestRefundTableComponent
  extends TypedBaseListComponent<
    typeof FetchRefundRequestsDocument,
    "refundRequests"
  >
  implements OnInit
{
  constructor(
    protected dataService: DataService,
    private currencyService: CurrencyService,
    private modalService: ModalService,
    private notiService: NotificationService
  ) {
    super();

    this.route.url.pipe(take(1)).subscribe((res) => {
      let routeFilter = "";
      if (res.length > 0) {
        const url = res[0];
        if (url.path.includes("admin")) {
          routeFilter = "Admin";
        }
      }

      super.configure({
        document: FetchRefundRequestsDocument,
        getItems: (data) => data.refundRequests,
        setVariables: (skip, take) => ({
          options: {
            skip,
            take,
            filter: {
              type: routeFilter ? { eq: routeFilter } : undefined,
              ...this.filters.createFilterInput(),
            },
            sort: this.sorts.createSortInput(),
          },
        }),
        refreshListOnChanges: [
          this.filters.valueChanges,
          this.sorts.valueChanges,
        ],
      });
    });
  }
  readonly filters = this.createFilterCollection()
    .addIdFilter()
    .addFilters([
      {
        filterField: "createdAt",
        label: "Creation Date",
        type: { kind: "dateRange" },
        name: "Creation Date",
      },
      {
        filterField: "status",
        label: "Status",
        name: "status",
        type: {
          kind: "select",
          options: [
            { value: "new", label: "New" },
            { value: "rejected", label: "Rejected" },
            { value: "approved", label: "Approved" },
            { value: "refunded", label: "Refunded" },
          ],
        },
      },
    ])
    .connectToRoute(this.route);

  readonly sorts = this.createSortCollection()
    .defaultSort("createdAt", "DESC")
    .connectToRoute(this.route);

  getQuantity(refundReq: any): number {
    return refundReq.lines.reduce((sum, line) => sum + line.quantity, 0);
  }

  getOrderTotal(refundReq: any) {
    const pipe = new LocaleCurrencyPipe(this.currencyService, this.dataService);
    const result = pipe.transform(refundReq.payment.amount, refundReq.order.currencyCode) as string;
    if(refundReq.order.currencyCode === CurrencyCode.KRW) {
      return result.split(".")[0]
    }
    return result
  }

  viewDetail(refundReq: any) {
    this.modalService
      .fromComponent(ModalRefundDetailComponent, {
        size: "xl",
        closable: true,
        locals: {
          refundReq: refundReq,
          currencyCode: refundReq.order.currencyCode,
        },
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          switch (result?.status) {
            case "CLOSE":
              break;
            case "REFUND_ORDER":
              this.refundOrder(result.order!, result.id, refundReq);
              break;
            default:
              this.setStatus(result.id, result.status.toLowerCase());
              break;
          }
        }
      });
  }

  setStatus(id: string, status: string) {
    this.dataService
      .mutate(SetRefundRequestStatusDocument, {
        input: {
          id,
          status,
        },
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result.updateRefundRequest.__typename === "RefundRequest") {
          this.notiService.success("Refund Request Updated");
        }
        this.refresh();
      });
  }

  refundOrder(order: OrderDetailFragment, refundId: string, refundReq?: any) {
    console.log(refundReq);
    const { args } = refundReq;

    if (args.refunds.length) {
      this.dataService.order
        .refundOrder(args.refunds[0])
        .subscribe(({ refundOrder }) => {
          if (refundOrder) {
            switch (refundOrder.__typename) {
              case "Refund":
                if (refundOrder.state === "Failed") {
                  this.notiService.error(_("order.refund-order-failed"));
                } else {
                  this.notiService.success(_("order.refund-order-success"));
                  this.setStatus(refundId, "refunded");

                  if (args.cancel.lines?.length) {
                    this.dataService.order
                      .cancelOrder(args.cancel)
                      .subscribe(({ cancelOrder }) => {
                        if (cancelOrder) {
                          switch (cancelOrder.__typename) {
                            case "Order":
                              this.notiService.success(
                                _("order.cancelled-order-success")
                              );
                              break;
                            case "CancelActiveOrderError":
                            case "QuantityTooGreatError":
                            case "MultipleOrderError":
                            case "OrderStateTransitionError":
                            case "EmptyOrderLineSelectionError":
                              this.notiService.error(cancelOrder.message);
                              break;
                          }
                        }
                      });
                  }
                }
                break;
              case "AlreadyRefundedError":
              case "NothingToRefundError":
              case "PaymentOrderMismatchError":
              case "RefundOrderStateError":
              case "RefundStateTransitionError":
                this.notiService.error(refundOrder.message);
                break;
            }
          }
        });
    }
  }
  //   this.modalService
  //     .fromComponent(RefundOrderDialogComponent, {
  //       size: "xl",
  //       closable: true,
  //       locals: {
  //         order,
  //       },
  //     })
  //     .pipe(
  //       switchMap((input) => {
  //         if (!input) {
  //           return of(undefined);
  //         }

  //         if (input.cancel.lines?.length) {
  //           return this.dataService.order.cancelOrder(input.cancel).pipe(
  //             map((res) => {
  //               const result = res.cancelOrder;
  //               switch (result.__typename) {
  //                 case "Order":
  //                   this.notiService.success(
  //                     _("order.cancelled-order-success")
  //                   );
  //                   this.setStatus(refundId, "refunded");
  //                   return input;
  //                 case "CancelActiveOrderError":
  //                 case "QuantityTooGreatError":
  //                 case "MultipleOrderError":
  //                 case "OrderStateTransitionError":
  //                 case "EmptyOrderLineSelectionError":
  //                   this.notiService.error(result.message);
  //                   return undefined;
  //               }
  //             })
  //           );
  //         } else {
  //           return [input];
  //         }
  //       }),
  //       switchMap((input) => {
  //         if (!input) {
  //           return of(undefined);
  //         }
  //         if (input.refunds.length) {
  //           // return this.dataService.order
  //           //   .refundOrder(input.refund)
  //           //   .pipe(map((res) => res.refundOrder));
  //           return this.dataService.order.refundOrder(input.refunds[0]).pipe(
  //             map((res) => res.refundOrder)
  //           );
  //         } else {
  //           return [undefined];
  //         }
  //       })
  //     )
  //     .subscribe((result) => {
  //       if (result) {
  //         switch (result.__typename) {
  //           case "Refund":
  //             if (result.state === "Failed") {
  //               this.notiService.error(_("order.refund-order-failed"));
  //             } else {
  //               this.notiService.success(_("order.refund-order-success"));
  //               this.setStatus(refundId, "refunded");
  //             }
  //             break;
  //           case "AlreadyRefundedError":
  //           case "NothingToRefundError":
  //           case "PaymentOrderMismatchError":
  //           case "RefundOrderStateError":
  //           case "RefundStateTransitionError":
  //             this.notiService.error(result.message);
  //             break;
  //         }
  //       }
  //     });
  // }
  
  bulkApprove() {
    const selectedItems = this.selectionManager.selection as any[];
    const newItems = selectedItems.filter((item) => item.status === "new");

    if (newItems.length === 0) {
      this.notiService.error(_("common.bulk-approve-no-new"));
      this.selectionManager.clearSelection();
      return;
    }

    const skippedCount = selectedItems.length - newItems.length;
    const bodyMsg = skippedCount > 0
      ? `${newItems.length} 个新退款申请将批准并直接退款，${skippedCount} 个非新状态的已跳过，确定继续吗？`
      : `${newItems.length} 个退款申请将被批准并直接退款，确定继续吗？`;

    this.modalService
      .dialog({
        title: _("common.bulk-approve-title"),
        body: bodyMsg,
        buttons: [
          { label: _("common.cancel"), type: "secondary", returnValue: false },
          { label: _("common.approve"), type: "primary", returnValue: true },
        ],
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          // Step 1: Approve all new items
          const approveObservables = newItems.map((item) =>
            this.dataService.mutate(SetRefundRequestStatusDocument, {
              input: { id: item.id, status: "approved" },
            })
          );

          forkJoin(approveObservables).subscribe(() => {
            // Step 2: Execute refund for each approved item sequentially
            let successCount = 0;
            let failCount = 0;
            const queue = [...newItems];

            const processNext = () => {
              if (queue.length === 0) {
                if (successCount > 0) {
                  this.notiService.success(`${successCount} 个退款申请已成功退款`);
                }
                if (failCount > 0) {
                  this.notiService.error(`${failCount} 个退款申请退款失败`);
                }
                this.selectionManager.clearSelection();
                this.refresh();
                return;
              }

              const item = queue.shift()!;
              const { args } = item;

              if (!args || !args.refunds || !args.refunds.length) {
                failCount++;
                processNext();
                return;
              }

              this.dataService.order
                .refundOrder(args.refunds[0])
                .pipe(take(1))
                .subscribe(({ refundOrder }) => {
                  if (refundOrder && refundOrder.__typename === "Refund") {
                    if (refundOrder.state === "Failed") {
                      failCount++;
                    } else {
                      successCount++;
                      // Set status to refunded
                      this.dataService.mutate(SetRefundRequestStatusDocument, {
                        input: { id: item.id, status: "refunded" },
                      }).pipe(take(1)).subscribe();

                      // Cancel order if needed
                      if (args.cancel && args.cancel.lines?.length) {
                        this.dataService.order.cancelOrder(args.cancel).pipe(take(1)).subscribe();
                      }
                    }
                  } else {
                    failCount++;
                    this.notiService.error(
                      `${item.order?.code || item.id}: ${(refundOrder as any)?.message || "退款失败"}`
                    );
                  }
                  processNext();
                }, () => {
                  failCount++;
                  processNext();
                });
            };

            processNext();
          });
        }
      });
  }

  bulkReject() {
    const selectedItems = this.selectionManager.selection as any[];
    const newItems = selectedItems.filter((item) => item.status === "new");

    if (newItems.length === 0) {
      this.notiService.error(_("common.bulk-approve-no-new"));
      this.selectionManager.clearSelection();
      return;
    }

    const skippedCount = selectedItems.length - newItems.length;
    const bodyMsg = skippedCount > 0
      ? `${newItems.length} 个新退款申请将被拒绝，${skippedCount} 个非新状态的已跳过，确定继续吗？`
      : `${newItems.length} 个退款申请将被拒绝，确定继续吗？`;

    this.modalService
      .dialog({
        title: _("common.bulk-reject-title"),
        body: bodyMsg,
        buttons: [
          { label: _("common.cancel"), type: "secondary", returnValue: false },
          { label: _("common.reject"), type: "danger", returnValue: true },
        ],
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          const observables = newItems.map((item) =>
            this.dataService.mutate(SetRefundRequestStatusDocument, {
              input: { id: item.id, status: "rejected" },
            })
          );
          forkJoin(observables).subscribe(() => {
            this.notiService.success(_("common.bulk-reject-success"));
            this.selectionManager.clearSelection();
            this.refresh();
          });
        }
      });
  }
}
